import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { AnalogTemperatureSensor, get_z3k_object_state, IOExtension, NTCTemperatureCurve, RadioSensor, } from 'z3k-config';
import { unpack_validation } from 'z3k-parser/validation/types';
import { findAndChangeLinks } from 'z3k-parser/lib/find-links';
import { getDeviceInputsOptions, hasNonEmptyOptions } from 'z3k-parser/lib/z3k-selectors';
import { AnalogTemperatureSensorStateSelector, WiredTemperatureSensorStateSelector, } from '../utils/z3k-state';
import { Divider } from '@mui/material';
import get_axis from 'console/tabs/graphs/components/graph/utils/axes';
import { useBox } from 'shared/hooks/utils';
import { Z3KSettings, Z3KSettingsEditor, Z3KSettingsState, } from 'console/device-settings/z3k/provider/types';
import { DragDropDrawer } from '../utils/drag-drop-drawer/component';
import { RouteNaming, Z3kSecondRoutes } from 'z3k-settings/routes/type';
import { RadioSensorsClickList } from '../radio/radiosensors/868/components/list';
import { Z3kDragDropContainer } from '../utils/drag-drop-drawer/container';
import { z3kOwaMultiplier } from 'z3k-parser/lib/owa';
import { owaAxis } from 'console/tabs/graphs/components/graph/utils/owa-axes';
import { getRadioRelays } from 'z3k-parser/lib/sensors/radio';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
import { temperature_from_state } from 'z3k-parser/lib';
export function TemperatureSensorsList() {
    const { config, replace_config } = useContext(Z3KSettingsEditor);
    const { z3k_state } = useContext(Z3KSettingsState);
    const { device_spec, addIdToBlacklist, serial } = useContext(Z3KSettings);
    const configBox = useBox(config);
    const onConfirmDeleteWTS = useCallback((sensor) => HeatingMode.get
        .where(configBox.current, ({ heating_zones }) => heating_zones.some(({ adjusting_sensor }) => adjusting_sensor == sensor.id))
        .map((mode) => `Режим отопления «${mode.name}» содержит ссылку на данный объект`), []);
    const onDeleteWTS = useCallback((sensor, new_list) => {
        addIdToBlacklist(sensor.id);
        replace_config(findAndChangeLinks({
            ...configBox.current,
            wired_temperature_sensors: new_list,
            heating_modes: HeatingMode.get
                .all(configBox.current)
                .map((mode) => ({
                ...mode,
                heating_zones: mode.heating_zones.map((zone) => ({
                    ...zone,
                    adjusting_sensor: zone.adjusting_sensor == sensor.id
                        ? null
                        : zone.adjusting_sensor,
                })),
            })),
        }, sensor.id));
    }, [replace_config, addIdToBlacklist]);
    const onAddATS = useCallback(() => {
        const [ntc_id, new_config] = NTCTemperatureCurve.get.add(configBox.current, {
            x: NTCTemperatureCurve.ntcDefaultX[AnalogTemperatureSensor.Type.NTC10],
            y: NTCTemperatureCurve.ntcDefaultY,
        });
        const [ats_id, config_with_ats] = AnalogTemperatureSensor.get.addDefault(new_config);
        replace_config(AnalogTemperatureSensor.get.applySetterById(config_with_ats, ats_id, {
            type: AnalogTemperatureSensor.Type.NTC10,
            ntc: ntc_id,
        }));
    }, [replace_config]);
    const onDeleteATS = useCallback((sensor, new_list) => {
        let new_config = configBox.current;
        if (sensor.ntc != null) {
            const found = sensor.type == AnalogTemperatureSensor.Type.OTHER
                ? 0
                : AnalogTemperatureSensor.get.where(new_config, (item) => item.type == sensor.type).length;
            if (found < 2) {
                addIdToBlacklist(sensor.ntc);
                new_config = NTCTemperatureCurve.get.removeById(new_config, sensor.ntc);
            }
        }
        addIdToBlacklist(sensor.id);
        replace_config({
            ...findAndChangeLinks(new_config, sensor.id),
            analog_temperature_sensors: new_list,
        });
    }, [addIdToBlacklist, replace_config]);
    const io_extensions = useMemo(() => IOExtension.get.all(config), [config]);
    const io_radio_relays = useMemo(() => getRadioRelays(config), [config]);
    const show_ntc_sensors = useMemo(() => hasNonEmptyOptions(getDeviceInputsOptions(serial, device_spec, io_extensions, io_radio_relays, true)), [device_spec, io_extensions, io_radio_relays]);
    const wired_temp_state = WiredTemperatureSensorStateSelector(config, z3k_state);
    const analog_temp_state = AnalogTemperatureSensorStateSelector(config, z3k_state);
    const titleComponentRender = useCallback((wts) => {
        const state = get_z3k_object_state(wired_temp_state, wts.id);
        if (!state) {
            return null;
        }
        const value = temperature_from_state(state);
        if (!value) {
            return _jsx("div", { children: "--" });
        }
        const axis = get_axis(owaAxis(wts.serial) ?? 'temperature');
        const owa_m = z3kOwaMultiplier(wts);
        const value_text = axis.format_value_with_unit(value * owa_m);
        return _jsx("div", { children: value_text });
    }, [device_spec]);
    const analogTitleComponentRender = useCallback((ats) => {
        const state = get_z3k_object_state(analog_temp_state, ats.id);
        if (!state) {
            return null;
        }
        const value = temperature_from_state(state);
        if (!value) {
            return _jsx("div", { children: "--" });
        }
        return _jsx("span", { style: { marginLeft: '2em' }, children: `${value.toFixed(1)}°` });
    }, [analog_temp_state]);
    const filterRadiosensors = useCallback((rs) => !RadioSensor.isFobButtons(rs) &&
        !RadioSensor.isRelaySensor(rs) &&
        RadioSensor.isThermometer(rs), []);
    const childrenItem = (selected_item_id) => {
        return (_jsxs(_Fragment, { children: [_jsx(DragDropDrawer, { z3k_name: "wired_temperature_sensors", name: RouteNaming[Z3kSecondRoutes.wired_temperature_sensors], help_id: "wired_temperature_sensors", enableDnd: true, item_url: Z3kSecondRoutes.wired_temperature_sensors, selected_item_id: selected_item_id, titleComponentRender: titleComponentRender, onDelete: onDeleteWTS, onConfirmDelete: onConfirmDeleteWTS }), show_ntc_sensors && (_jsx(DragDropDrawer, { z3k_name: "analog_temperature_sensors", name: RouteNaming[Z3kSecondRoutes.analog_temperature_sensors], help_id: "analog_temperature_sensors", enableDnd: true, item_url: Z3kSecondRoutes.analog_temperature_sensors, selected_item_id: selected_item_id, titleComponentRender: analogTitleComponentRender, onAddClick: onAddATS, onDelete: onDeleteATS })), _jsx(Divider, {}), _jsx(RadioSensorsClickList, { name: RouteNaming[Z3kSecondRoutes.radiosensor868], objectFilter: filterRadiosensors, help_id: "temperature_radiosensors" })] }));
    };
    return _jsx(Z3kDragDropContainer, { childrenItem: childrenItem });
}
const fields = [
    'analog_temperature_sensors',
    'wired_temperature_sensors',
    'radiosensors',
];
TemperatureSensorsList.isError = (validation_result) => fields.reduce((prev, current) => prev || !!unpack_validation(validation_result, current), false);
TemperatureSensorsList.isWarning = (validation_warning_result) => fields.reduce((prev, current) => prev || !!unpack_validation(validation_warning_result, current), false);
TemperatureSensorsList.isShown = (is_service_view, config, device_spec, is_staff, is_climatic) => !is_climatic && device_spec.io.has_1wire;
